import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Upload } from "lucide-react";
import client from "@/lib/api";
import { SectionHeader, EmptyState, notifyError, notifyOk } from "./_ui";
import SpecsEditor from "./SpecsEditor";
import { isValidVin, VIN_HINT } from "@/lib/vin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const emptyCard = { brand_id: "", vin: "", specs: [], status: "free" };

export default function Cards() {
  const [cards, setCards] = useState([]);
  const [brands, setBrands] = useState([]);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState(emptyCard);
  const [editing, setEditing] = useState(null);
  const [bulk, setBulk] = useState({ brand_id: "", raw: "" });

  const load = () => client.get("/admin/cards").then((r) => setCards(r.data)).catch(notifyError);
  useEffect(() => {
    load();
    client.get("/admin/brands").then((r) => setBrands(r.data)).catch(notifyError);
  }, []);

  const brandName = (id) => brands.find((b) => b.id === id)?.name || "—";

  const openNew = () => { setForm({ ...emptyCard, brand_id: brands[0]?.id || "" }); setEditing(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...c, specs: c.specs || [] }); setEditing(c.id); setOpen(true); };

  const save = async () => {
    if (!isValidVin(form.vin)) { notifyError({ message: "Неверный формат VIN. " + VIN_HINT }); return; }
    try {
      if (editing) await client.put(`/admin/cards/${editing}`, form);
      else await client.post("/admin/cards", form);
      setOpen(false); notifyOk("Сохранено"); load();
    } catch (e) { notifyError(e); }
  };

  const saveBulk = async () => {
    try {
      const { data } = await client.post("/admin/cards/bulk", bulk);
      setBulkOpen(false); setBulk({ brand_id: "", raw: "" });
      notifyOk(`Добавлено карточек: ${data.created}`); load();
    } catch (e) { notifyError(e); }
  };

  const remove = async (id) => {
    if (!window.confirm("Удалить карточку?")) return;
    await client.delete(`/admin/cards/${id}`); load();
  };

  return (
    <div>
      <SectionHeader
        title="Карточки авто (пул)"
        description="Заранее подготовленные карточки. При создании акта берётся первая свободная карточка марки."
        action={
          <div className="flex gap-2">
            <Button variant="outline" data-testid="cards-bulk-btn" onClick={() => { setBulk({ brand_id: brands[0]?.id || "", raw: "" }); setBulkOpen(true); }}>
              <Upload className="w-4 h-4 mr-1.5" /> Импорт списком
            </Button>
            <Button data-testid="card-add-btn" onClick={openNew} style={{ background: "var(--ci-accent)", color: "#1a1a1a" }}>
              <Plus className="w-4 h-4 mr-1.5" /> Карточка
            </Button>
          </div>
        }
      />

      {cards.length === 0 ? (
        <EmptyState text="Карточек пока нет." />
      ) : (
        <div className="border border-neutral-200 rounded-lg bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Марка</TableHead>
                <TableHead>VIN</TableHead>
                <TableHead>Строк данных</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead className="text-right">Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cards.map((c) => (
                <TableRow key={c.id} data-testid={`card-row-${c.id}`}>
                  <TableCell>{c.order}</TableCell>
                  <TableCell>{brandName(c.brand_id)}</TableCell>
                  <TableCell className="font-mono text-xs">{c.vin}</TableCell>
                  <TableCell>{(c.specs || []).length}</TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${c.status === "free" ? "bg-green-100 text-green-700" : "bg-neutral-200 text-neutral-600"}`}>
                      {c.status === "free" ? "свободна" : "использована"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <button onClick={() => openEdit(c)} className="inline-flex p-1.5 text-neutral-600 hover:text-black" data-testid={`card-edit-${c.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(c.id)} className="inline-flex p-1.5 text-red-600" data-testid={`card-delete-${c.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Редактировать карточку" : "Новая карточка"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Марка</Label>
              <Select value={form.brand_id} onValueChange={(v) => setForm({ ...form, brand_id: v })}>
                <SelectTrigger data-testid="card-brand-select"><SelectValue placeholder="Выберите марку" /></SelectTrigger>
                <SelectContent>
                  {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>VIN</Label>
              <Input data-testid="card-vin-input" value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })} className={`border-neutral-300 font-mono ${form.vin && !isValidVin(form.vin) ? "border-red-500 focus-visible:ring-red-500" : ""}`} />
              {form.vin && !isValidVin(form.vin) && (
                <p className="text-xs text-red-600" data-testid="card-vin-error">{VIN_HINT}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Тех.характеристики</Label>
              <SpecsEditor specs={form.specs} onChange={(s) => setForm({ ...form, specs: s })} testid="card-specs" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Отмена</Button>
            <Button data-testid="card-save-btn" onClick={save} style={{ background: "var(--ci-accent)", color: "#1a1a1a" }}>Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Импорт карточек списком</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Марка</Label>
              <Select value={bulk.brand_id} onValueChange={(v) => setBulk({ ...bulk, brand_id: v })}>
                <SelectTrigger data-testid="bulk-brand-select"><SelectValue placeholder="Выберите марку" /></SelectTrigger>
                <SelectContent>
                  {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Список</Label>
              <p className="text-xs text-neutral-500">Каждая строка: <code>VIN | Метка: значение ; Метка: значение</code></p>
              <Textarea
                data-testid="bulk-raw"
                rows={8}
                value={bulk.raw}
                onChange={(e) => setBulk({ ...bulk, raw: e.target.value })}
                className="border-neutral-300 font-mono text-xs"
                placeholder={"WBA123... | Цвет: Чёрный ; Двигатель: 3.0 R6\nWBA456... | Цвет: Белый"}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Отмена</Button>
            <Button data-testid="bulk-save-btn" onClick={saveBulk} style={{ background: "var(--ci-accent)", color: "#1a1a1a" }}>Импортировать</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
